import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import {
  Card, Spinner, Modal, ModalHeader, ModalBody, Button, Row, Col,
} from "reactstrap";
import Header from "components/Headers/Header";
import ReuseableTable from "components/Tables/ReuseableTable";
import FanOpinionsModal from "components/modals/FanOpinionsModal";
import {
  fetchBulletinBoard,
  loadMoreBulletinBoard,
} from "../../store/actions/bulletinBoardAction";
import noticeBulletin from "../../assets/img/custom/noticeBulletin.png";
import VisibilityExplainerVideos from "../../components/VisibilityExplainerVideos";

// ─── Profile Modal ────────────────────────────────────────────────────────────
const ProfileModal = ({ isOpen, toggle, profile, isManager }) => {
  const userProfile = profile;

  const renderField = (label, value, small = false) => {
    if (value === undefined || value === null || value === "") return null;
    return (
      <Col xs="12" sm="6" className="mb-2">
        <small style={{ fontSize: 10, color: "#8898aa", display: "block", marginBottom: 2 }}>
          {label}
        </small>
        <span style={{ fontWeight: 600, fontSize: small ? 11 : 13, color: "#32325d", display: "block" }}>
          {String(value)}
        </span>
      </Col>
    );
  };

  const renderSection = (title, children) => (
    <div style={{ background: "#f8f9fa", borderRadius: 12, padding: "12px 16px", marginBottom: 14 }}>
      <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: "#8898aa", marginBottom: 10 }}>
        {title}
      </p>
      <Row>{children}</Row>
    </div>
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable>
      <ModalHeader toggle={toggle} style={{ borderBottom: "none", paddingBottom: 0 }}>
        User Profile
      </ModalHeader>
      <ModalBody style={{ padding: "0 24px 24px" }}>
        {!userProfile ? (
          <p className="text-center text-muted py-5">No profile found.</p>
        ) : (
          <>
            <div style={{
              background: "linear-gradient(135deg, #5e72e4 0%, #825ee4 100%)",
              borderRadius: 16, padding: "28px 20px 20px",
              textAlign: "center", marginBottom: 20, color: "#fff",
            }}>
              <img
                src={isManager ? (userProfile.photo || "https://via.placeholder.com/90") : (userProfile.player_image?.url || "https://via.placeholder.com/90")}
                alt="profile"
                style={{ width: 88, height: 88, borderRadius: "50%", objectFit: "cover", border: "3px solid rgba(255,255,255,0.6)", marginBottom: 10 }}
              />
              <h3 style={{ color: "#fff", marginBottom: 2 }}>
                {isManager ? userProfile.name : `${userProfile.first_name || ""} ${userProfile.last_name || ""}`.trim()}
              </h3>
              <p style={{ opacity: 0.8, fontSize: 13, marginBottom: 10 }}>{userProfile.email}</p>
              <div style={{ marginBottom: 8 }}>
                {userProfile.role && <span style={{ background: "rgba(255,255,255,0.2)", borderRadius: 20, padding: "3px 12px", fontSize: 11, fontWeight: 600, marginRight: 6 }}>{userProfile.role}</span>}
                {!isManager && userProfile.sportLevel && <span style={{ background: "rgba(255,255,255,0.2)", borderRadius: 20, padding: "3px 12px", fontSize: 11, fontWeight: 600, marginRight: 6 }}>{userProfile.sportLevel}</span>}
                {!isManager && userProfile.sport && <span style={{ background: "rgba(255,255,255,0.2)", borderRadius: 20, padding: "3px 12px", fontSize: 11, fontWeight: 600 }}>{userProfile.sport}</span>}
              </div>
              {/* ID badge — matches what's shown in the table */}
              {!isManager && userProfile.athleteID && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.4)", borderRadius: 8, padding: "5px 14px", marginTop: 4 }}>
                  <span style={{ fontSize: 11, opacity: 0.8, fontWeight: 500 }}>Athlete ID:</span>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{userProfile.athleteID}</span>
                </div>
              )}
              {isManager && userProfile.managerID && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.4)", borderRadius: 8, padding: "5px 14px", marginTop: 4 }}>
                  <span style={{ fontSize: 11, opacity: 0.8, fontWeight: 500 }}>Manager ID:</span>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{userProfile.managerID}</span>
                </div>
              )}
            </div>

            {isManager ? (
              <>
                {renderSection("Personal Info", <>
                  {renderField("Name", userProfile.name)}
                  {renderField("Gender", userProfile.gender)}
                  {renderField("Phone", userProfile.phoneNumber)}
                  {renderField("City", userProfile.city)}
                  {renderField("State", userProfile.state)}
                  {renderField("Zip Code", userProfile.zipCode)}
                  {renderField("Family Status", userProfile.familyStatus)}
                  {renderField("Income", userProfile.income)}
                  {renderField("Education", userProfile.education)}
                  {renderField("Description", userProfile.description)}
                </>)}
                {renderSection("Manager Info", <>
                  {renderField("Manager ID", userProfile.managerID)}
                  {renderField("Fan ID", userProfile.fanID)}
                  {renderField("Chat Rate", userProfile.chatRate ? `${userProfile.chatRate}` : null)}
                  {renderField("Paypal Email", userProfile.paypal_email, true)}
                  {renderField("Secondary Paypal", userProfile.secondaryPaypalEmail, true)}
                  {renderField("School(s)", Array.isArray(userProfile.school) ? userProfile.school.join(", ") : userProfile.school, true)}
                </>)}
                {renderSection("Stats", <>
                  {renderField("Total Complete Chats", userProfile.total_complete_chats)}
                  {renderField("Certification Score", userProfile.certificationScore)}
                  {renderField("Registration Amount", userProfile.registration_amount)}
                </>)}
              </>
            ) : (
              <>
                {renderSection("Personal Info", <>
                  {renderField("First Name", userProfile.first_name)}
                  {renderField("Last Name", userProfile.last_name)}
                  {renderField("Gender", userProfile.gender)}
                  {renderField("Date of Birth", userProfile.datOffBirth)}
                  {renderField("Phone", userProfile.phoneNumber)}
                  {renderField("City", userProfile.city)}
                  {renderField("State", userProfile.state)}
                  {renderField("Address", userProfile.address)}
                </>)}
                {renderSection("Sport Info", <>
                  {renderField("Sport", userProfile.sport)}
                  {renderField("Sport Level", userProfile.sportLevel)}
                  {renderField("Chat Rate", userProfile.chatRate ? `${userProfile.chatRate}` : null)}
                  {renderField("Sport Badge Rate", userProfile.sportBadgeRate)}
                  {renderField("Overall Rating", userProfile.overAllRating)}
                  {renderField("Athlete ID", userProfile.athleteID)}
                </>)}
                {renderSection("School Info", <>
                  {renderField("Institute", userProfile.institute_name)}
                </>)}
                {renderSection("Stats", <>
                  {renderField("Talking Badge", userProfile.talkingBadge)}
                  {renderField("Seasonal Badge", userProfile.seasonalBadge)}
                  {renderField("Total Seasonal Badges", userProfile.totalSeasonalBadges)}
                  {renderField("Momento Count", userProfile.momentoCount)}
                  {renderField("Chat Count", userProfile.chat_count)}
                  {renderField("Total Complete Chats", userProfile.total_complete_chats)}
                  {renderField("Certification Score", userProfile.certificationScore)}
                </>)}
              </>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

// ─── Visibility Widget (inline) ───────────────────────────────────────────────
const VisibilityWidget = ({ onSelect }) => {
  const sections = [
    { line1: "ATHLETES AT TOP",     line2: "",               tab: "Boosted" },
    { line1: "YOUR FAN OPINION",    line2: "BOOSTS ATHLETE", tab: "Opinion" },
    { line1: "FAN MANAGERS AT TOP", line2: "",               tab: "Managers" },
  ];

  const ArrowPair = () => (
    <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
      <div style={{ width: 0, height: 0, borderTop: "10px solid transparent", borderBottom: "10px solid transparent", borderLeft: "13px solid #e00" }} />
      <div style={{ width: 0, height: 0, borderTop: "10px solid transparent", borderBottom: "10px solid transparent", borderLeft: "13px solid #e00" }} />
    </div>
  );

  return (
    <div style={{ width: "360px" }}>
      {/* Card */}
      <div style={{
        border: "3px solid #222",
        borderRadius: "14px",
        overflow: "hidden",
        backgroundColor: "#fff",
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
      }}>
        {/* Header */}
        <div style={{ padding: "12px 14px" }}>
          <div style={{ border: "3px solid #e00", borderRadius: "8px", padding: "12px 10px", textAlign: "center" }}>
            <div style={{ fontSize: "20px", fontWeight: "800", color: "#4a5568", letterSpacing: "1.5px", lineHeight: 1.2 }}>
              VISIBILITYBOARDS
            </div>
          </div>
        </div>

        {sections.map((section, i) => (
          <div
            key={i}
            onClick={() => onSelect(section.tab)}
            style={{
              borderTop: "2px solid #bbb",
              padding: "12px 16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "pointer",
              backgroundColor: "#fff",
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f5f5f5"}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#fff"}
          >
            <div>
              <div style={{ fontSize: "16px", fontWeight: "800", color: "#e00", lineHeight: 1.3 }}>{section.line1}</div>
              <div style={{ fontSize: "16px", fontWeight: "800", color: "#3b5bdb", lineHeight: 1.3 }}>{section.line2}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 80 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ fontSize: "15px", fontWeight: "800", color: "#222" }}>VIEW</span>
                <ArrowPair />
              </div>
              <span style={{ fontSize: "15px", fontWeight: "800", color: "#222" }}>BOARD</span>
            </div>
          </div>
        ))}

        {/* Footer */}
        <div style={{ borderTop: "2px solid #bbb", padding: "12px", textAlign: "center" }}>
          <span style={{ fontSize: "16px", fontWeight: "800", color: "#e00" }}>CLICK </span>
          <span style={{ fontSize: "16px", fontWeight: "800", color: "#222" }}>ARROW</span>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const BulletinBoard = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { loading, loadingMore, boostedAthletes, boostedManagers, shoutOutAthletes, hasMore } =
    useSelector((state) => state.bulletinBoard);

  const [activeTab, setActiveTab] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [opinionModal, setOpinionModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [profileRow, setProfileRow] = useState(null);

  // Keep URL in sync whenever the user picks a tab inside the page
  const handleSetActiveTab = (tab) => {
    if (tab) {
      history.replace({ search: `?tab=${encodeURIComponent(tab)}` });
    } else {
      history.replace({ search: "" });
    }
    setActiveTab(tab);
  };

  // Sync activeTab from URL on every navigation (handles refresh + links from other pages)
  useEffect(() => {
    const queryTab = new URLSearchParams(location.search).get("tab");
    const stateTab = location.state?.activeTab;
    setActiveTab(queryTab || stateTab || null);
  }, [location.search, location.state]);

  // Re-fetch fresh data every time a tab becomes active
  useEffect(() => {
    if (activeTab) {
      dispatch(fetchBulletinBoard());
    }
  }, [activeTab, dispatch]);

  const openModal = (row, managerTab) => {
    setIsManager(managerTab);
    setProfileRow(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setProfileRow(null);
  };

  // Updated columns per tab
  const athleteColumns = ["Athlete Name", "Athlete ID"];
  const athleteKeyMap = {
    "Athlete Name": "name",
    "Athlete ID": "athleteID",
  };

  const managerColumns = ["Manager Name", "Manager ID"];
  const managerKeyMap = {
    "Manager Name": "name",
    "Manager ID": "managerID",
  };

  const opineColumns = ["Athlete Name", "Athlete ID", "Opinion"];
  const opineKeyMap = {
    "Athlete Name": "name",
    "Athlete ID": "athleteID",
  };

  const tabs = [
    { label: "Boosted",   reduxKey: "boostedAthletes",  data: boostedAthletes,  isManager: false, columns: athleteColumns, keyMap: athleteKeyMap },
    { label: "Managers",  reduxKey: "boostedManagers",   data: boostedManagers,  isManager: true,  columns: managerColumns, keyMap: managerKeyMap },
    { label: "Opinion",   reduxKey: "shoutOutAthletes",  data: shoutOutAthletes, isManager: false, columns: opineColumns,   keyMap: opineKeyMap   },
  ];

  const activeTabData = tabs.find((t) => t.label === activeTab);

  return (
    <>
      <Header />

      {/* Widget + image — hidden once a tab is selected */}
      {!activeTab && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0", flexWrap: "wrap", padding: "20px 40px" }}>
            <div style={{ flex: 1, minWidth: "300px", display: "flex", justifyContent: "center" }}>
              <VisibilityWidget onSelect={(tab) => handleSetActiveTab(tab)} />
            </div>
            <div style={{ flex: 1, minWidth: "300px", display: "flex", justifyContent: "center" }}>
              <img
                src={noticeBulletin}
                alt="Visibility Board Notice"
                style={{ width: "100%", maxWidth: "420px", borderRadius: "12px", objectFit: "contain" }}
              />
            </div>
          </div>
          {/* Explainer videos box — centered below the widget row */}
          <div style={{ display: "flex", justifyContent: "center", paddingBottom: "20px" }}>
            <VisibilityExplainerVideos />
          </div>
        </>
      )}

      {/* Table section — only shown after a tab is selected */}
      {activeTab && activeTabData && (
        <>
          {/* Back button — styled, outside and above the card */}
          <div className="px-4 pt-4 pb-2 d-flex align-items-center" style={{ gap: "16px" }}>
            <Button
              onClick={() => handleSetActiveTab(null)}
              size="md"
              color="secondary"
            >
              &#8592; Back
            </Button>
          </div>
<div className="mx-5 my-4">

            <h3 className="mb-0" style={{ color: "#32325d", fontWeight: 700 }}>
              <span style={{ color: "#e00", marginRight: "8px" }}>HONOR</span>
              {activeTab}
            </h3>
</div>
          <Card className="mx-4 mb-4">
            {loading ? (
              <div className="text-center py-4"><Spinner /></div>
            ) : (
              <ReuseableTable
                columns={activeTabData.columns}
                keyMap={activeTabData.keyMap}
                data={activeTabData.data}
                renderCell={activeTab === "Opinion" ? (column, row) => {
                  if (column === "Opinion") {
                    return (
                      <Button
                        size="sm"
                        color="primary"
                        onClick={() => { setSelectedRow(row); setOpinionModal(true); }}
                      >
                        View
                      </Button>
                    );
                  }
                  return null;
                } : undefined}
                renderActions={(row) => (
                  <Button size="sm" color="primary" style={{ whiteSpace: "nowrap" }} onClick={() => openModal(row, activeTabData.isManager)}>
                    View Details
                  </Button>
                )}
              />
            )}
          </Card>

          {/* Load More — outside the card */}
          {!loading && (
            <div className="d-flex justify-content-center mb-4">
              <Button
                color="primary"
                disabled={!hasMore[activeTabData.reduxKey] || loadingMore}
                onClick={() => dispatch(loadMoreBulletinBoard(activeTabData.reduxKey))}
                style={{ minWidth: 140, height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                {loadingMore ? (
                  <Spinner size="sm" />
                ) : !hasMore[activeTabData.reduxKey] ? (
                  "No More Records"
                ) : (
                  "Load More"
                )}
              </Button>
            </div>
          )}
        </>
      )}

      <ProfileModal isOpen={modalOpen} toggle={closeModal} profile={profileRow} isManager={isManager} />

      <FanOpinionsModal
        isOpen={opinionModal}
        toggle={() => setOpinionModal(false)}
        boostDocId={selectedRow?.id}
        latestOpinion={selectedRow?.opinion}
      />
    </>
  );
};

export default BulletinBoard;
